import React from "react";
import { Box, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { TruncatedTextWithTooltip } from "../../components/ui/TruncatedTextWithTooltip";
import greenCheckVerified from "../../assets/svgIcons/green-check-verified.svg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// --- Interfaces ---
export interface DetailItem {
  label: string | React.ReactNode;
  value?: string | number | boolean | React.ReactNode;
  isVerified?: boolean;
  /** When true and the row has a value, show a "Not verified" tag (e.g. email present but flag false). */
  showNotVerified?: boolean;
  /** Where to show Verified / Not Verified chips. Default: value row only. */
  verificationPlacement?: "value" | "label" | "both";
}

interface DetailRowProps {
  item: DetailItem;
  showInfoIcon?: boolean;
  salespersonTooltipContent?: any;
}

const DetailRow: React.FC<DetailRowProps> = ({
  item,
  showInfoIcon,
  salespersonTooltipContent,
}) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("md"));
  const {
    label,
    value,
    isVerified,
    showNotVerified,
    verificationPlacement = "value",
  } = item;
  const labelIsString = typeof label === "string";
  const labelStrForKeywords = labelIsString ? label : String(label ?? "");

  const showVerificationInLabel =
    verificationPlacement === "label" || verificationPlacement === "both";
  const showVerificationInValue =
    verificationPlacement === "value" || verificationPlacement === "both";
  const normalizedValue =
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    !React.isValidElement(value)
      ? ((value as any).label ??
        (value as any).value ??
        "")
      : value;
  let displayValue: React.ReactNode = normalizedValue;

  // 1. Define keywords for detection
  const currencyKeywords = [
    "amount",
    "price",
    "fees",
    "cost",
    "loan",
    "charge",
    "balance",
    "expense",
    "income",
    "obligation",
    "own sources",
    "collateral value"
  ];

  // Fix: Use the array to check against the label
  const isCurrencyField = currencyKeywords.some((keyword) => labelStrForKeywords?.toLowerCase().includes(keyword) && (!(label as string)?.includes("Loan Tenure") && !(label as string)?.includes("Requested Roi"))
  );

  // NEW: Check if the label refers to a PAN card
  const isPanField = labelStrForKeywords?.toLowerCase().includes("pan");

  // 2. Clean the value for numeric checking
  const cleanValue =
    typeof normalizedValue === "string"
      ? normalizedValue.replace(/[₹\u00A0,]/g, "").trim()
      : normalizedValue;

  // 3. Validation Logic
  const hasActualData =
    normalizedValue !== null && normalizedValue !== "" && normalizedValue !== "-";
  const isValidNumber =
    !isNaN(Number(cleanValue)) && isFinite(Number(cleanValue));

  // 4. Formatting Logic
  if (
    isPanField &&
    typeof normalizedValue === "string" &&
    normalizedValue.length === 10
  ) {
    /**
     * PAN Masking Logic:
     * Takes first 5 chars, replaces next 4 with asterisks, keeps the last char.
     * Example: ABCDE1234F -> ABCDE****F
     */
    displayValue = normalizedValue
      .toUpperCase()
      .replace(/^(.{5}).{4}(.{1})$/, "$1****$2");
  } else if (isCurrencyField && hasActualData && isValidNumber) {
    displayValue = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
      .format(Number(cleanValue))
      .replace("₹", "₹\u00A0");
  } else {
    displayValue = normalizedValue || "-";
  }

  // 5. Determine Text Transform logic
  const getTextTransform = () => {
    if (isPanField) return "uppercase"; // Keep PAN format intact
    if (labelStrForKeywords.toLowerCase().includes("email")) return "lowercase";
    return "capitalize";
  };

  const renderVerificationChips = () => (
    <>
      {isVerified && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: "#28C76F",
            fontSize: isXsOrSm ? "12px" : "13px",
            flexShrink: 0,
          }}
        >
          <img
            src={greenCheckVerified}
            alt="Verified"
            style={{
              height: isXsOrSm ? "13px" : "15px",
              objectFit: "contain",
              imageRendering: "-webkit-optimize-contrast",
            }}
          />
          <Typography variant="inherit" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Verified
          </Typography>
        </Box>
      )}
      {!isVerified && showNotVerified && hasActualData && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1,
            py: 0.35,
            borderRadius: "4px",
            bgcolor: "rgba(255, 159, 67, 0.14)",
            border: "1px solid rgba(255, 159, 67, 0.45)",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="inherit"
            sx={{
              color: "#ff9f43",
              fontSize: isXsOrSm ? "11px" : "12px",
              fontWeight: 600,
              lineHeight: 1.2,
            }}
          >
            Not Verified
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <Box className="card-contaner-section">
      {/* Label Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          marginBottom: "10px",
        }}
      >
        <Typography
          component="span"
          style={{
            color: "#a5a3ae",
            fontFamily: "Jost",
            fontWeight: "400",
            fontSize: "13px",
            lineHeight: "14px",
            textTransform: "capitalize",
            marginRight: showInfoIcon ? "4px" : "0",
          }}
        >
          {label}
        </Typography>

        {showVerificationInLabel && renderVerificationChips()}

        {showInfoIcon && (
          <Tooltip
            title={salespersonTooltipContent}
            placement="top-start"
            arrow
          >
            <IconButton
              size="small"
              sx={{
                padding: 0,
                "&:hover": { background: "transparent" },
              }}
              aria-label="Salesperson contact details"
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: "14px",
                  color: "#a5a3ae",
                  cursor: "pointer",
                }}
              />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Value Section */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {!isCurrencyField &&
        !isPanField &&
        typeof displayValue === "string" &&
        isNaN(Number(cleanValue)) ? (
          <TruncatedTextWithTooltip
            style={{
              color: "#6f6b7d",
              fontFamily: "Jost",
              fontWeight: "700",
              fontSize: "12px",
              lineHeight: "20px",
              textTransform: getTextTransform(),
            }}
            text={String(displayValue || "")}
            // limit={35}
          />
        ) : (
          <Typography
            style={{
              color: "#6f6b7d",
              fontFamily: "Jost",
              fontWeight: "700",
              fontSize: "12px",
              lineHeight: "20px",
              textTransform: getTextTransform(),
            }}
          >
            {displayValue}
          </Typography>
        )}

        {showVerificationInValue && renderVerificationChips()}
      </Box>
    </Box>
  );
};

export default DetailRow;
